import {
  type Chain,
  type DynamicClient,
  type WalletProviderMetadata,
  assertWalletAccountSigningAvailability,
} from '@dynamic-labs-sdk/client';
import {
  DYNAMIC_ICONIC_SPRITE_URL,
  assertDefined,
  createWalletProviderEventEmitter,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { Psbt } from 'bitcoinjs-lib';

import type {
  BitcoinSendTransactionResponse,
  BitcoinWalletProvider,
  SignPsbtOptions,
} from '../../../../BitcoinWalletProvider.types';
import { bitcoinExecuteCheckoutTransaction } from '../../../../utils/bitcoinExecuteCheckoutTransaction';
import { bitcoinTransferAmount } from '../../../../utils/bitcoinTransferAmount';
import { createPsbtOptions } from '../../../../utils/createPsbtOptions';
import { getNetworkIdFromNetworkName } from '../../../../utils/getNetworkIdFromNetworkName';
import type { OkxBitcoinInjectedProvider } from '../OkxBitcoinInjectedProvider.types';

export const OKX_METADATA: WalletProviderMetadata = {
  displayName: 'OKX Wallet',
  icon: `${DYNAMIC_ICONIC_SPRITE_URL}#okx`,
};

type CreateOkxBitcoinWalletProviderParams = {
  dynamicClient: DynamicClient;
  injectedProvider: OkxBitcoinInjectedProvider;
};

export const createOkxBitcoinWalletProvider = ({
  dynamicClient,
  injectedProvider,
}: CreateOkxBitcoinWalletProviderParams): BitcoinWalletProvider => {
  const chain: Chain = 'BTC';

  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain,
    displayName: OKX_METADATA.displayName,
    walletProviderType,
  });

  const connect: BitcoinWalletProvider['connect'] = async () => {
    const { address, publicKey } = await injectedProvider.connect();

    return {
      addresses: [{ address, publicKey }],
    };
  };

  const getActiveNetworkId: BitcoinWalletProvider['getActiveNetworkId'] =
    async () => {
      const network = await injectedProvider.getNetwork();

      return getNetworkIdFromNetworkName(
        { networkName: network },
        dynamicClient
      );
    };

  const getConnectedAddresses: BitcoinWalletProvider['getConnectedAddresses'] =
    async () => {
      const accounts = await injectedProvider.getAccounts();

      return {
        addresses: accounts,
      };
    };

  const sendBitcoin: BitcoinWalletProvider['sendBitcoin'] = async ({
    transaction,
    walletAccount,
  }): Promise<BitcoinSendTransactionResponse> => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const transactionId = await injectedProvider.sendBitcoin(
      transaction.recipientAddress,
      transaction.amount.toString()
    );

    return {
      transactionId,
    };
  };

  const signMessage: BitcoinWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
    protocol,
  }) => {
    assertDefined(walletAccount, 'Wallet account not found');

    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const signedMessage = await injectedProvider.signMessage(message, protocol);

    return {
      signature: signedMessage,
    };
  };

  const signPsbt: BitcoinWalletProvider['signPsbt'] = async ({
    request,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const psbtFromBase64 = Psbt.fromBase64(request.unsignedPsbtBase64);

    const signedPsbtHex = await injectedProvider.signPsbt(
      psbtFromBase64.toHex(),
      createPsbtOptions({ psbt: psbtFromBase64, request })
    );

    return { signedPsbt: Psbt.fromHex(signedPsbtHex).toBase64() };
  };

  const signPsbts: BitcoinWalletProvider['signPsbts'] = async ({
    requests,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const psbtHexs: string[] = [];
    const psbtOptions: SignPsbtOptions[] = [];

    for (const request of requests) {
      const psbtFromBase64 = Psbt.fromBase64(request.unsignedPsbtBase64);

      psbtHexs.push(psbtFromBase64.toHex());
      psbtOptions.push(createPsbtOptions({ psbt: psbtFromBase64, request }));
    }

    const signedPsbtHexs = await injectedProvider.signPsbts(
      psbtHexs,
      psbtOptions
    );

    return {
      signedPsbts: signedPsbtHexs.map((signedPsbtHex) =>
        Psbt.fromHex(signedPsbtHex).toBase64()
      ),
    };
  };

  const { getEventEmitter, cleanupEventEmitter } =
    createWalletProviderEventEmitter({
      removeEventListeners: () => {
        injectedProvider?.removeAllListeners();
      },

      setupEventListeners: ({ handleAccountsChanged }) => {
        injectedProvider?.on('accountsChanged', (addresses) => {
          handleAccountsChanged({ addresses });
        });
      },

      supportedEvents: ['accountsChanged'],
    });

  const terminate: BitcoinWalletProvider['terminate'] = async () => {
    cleanupEventEmitter();
  };

  return {
    chain,
    connect,
    get events() {
      return getEventEmitter();
    },
    executeCheckoutTransaction: (args) =>
      bitcoinExecuteCheckoutTransaction(args, dynamicClient),
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(OKX_METADATA.displayName),
    key,
    metadata: OKX_METADATA,
    sendBitcoin,
    signMessage,
    signPsbt,
    signPsbts,
    terminate,
    transferAmount: (args) => bitcoinTransferAmount(args, dynamicClient),
    walletProviderType,
  };
};
